/**
 * Who sees Copilot (spec §5.1), as a pure function; useCopilotAvailability feeds it from storeMain.
 * Copilot has no switch, global or per tenant:
 * 1. an activated user;
 * 2. a Copilot licence (copilotLicence);
 * 3. consent (the Graph token carries the Copilot scopes).
 * Either of 1-2 failing, or unknown: hidden. Only 3 failing: the consent notice for a tenant admin,
 * hidden for anyone else. Consent unknown (an auth state persisted before the API reported it):
 * hidden until the next token refresh says.
 */
export type CopilotAvailability = "hidden" | "needsConsent" | "ready";

export interface ICopilotAvailabilityInputs {
  isActivated: boolean;
  hasLicence: boolean | undefined;
  scopesGranted: boolean | undefined;
  isTenantAdmin: boolean;
}

export const getCopilotAvailability = (inputs: ICopilotAvailabilityInputs): CopilotAvailability => {
  if (!inputs || inputs.isActivated !== true || inputs.hasLicence !== true) {
    return "hidden";
  }
  if (inputs.scopesGranted === true) {
    return "ready";
  }
  return inputs.scopesGranted === false && inputs.isTenantAdmin === true ? "needsConsent" : "hidden";
};

const listeners: (() => void)[] = [];

/** copilotScopesGranted lives in AuthModule, not Redux, and any Graph refresh can change it:
 * AuthModule calls this when one does (configureAndPersistRefreshedAuthState, Step 6). */
// Runs the listeners after the current task, each on its own (audit 2026-10-19): AuthModule calls
// this from inside a token refresh, and a listener re-renders a whole host, so a render error
// there must not land in the refresh's catch (which signs the user out), nor hold the refresh up.
export const notifyCopilotAvailabilityChanged = (): void => {
  setTimeout(() => {
    listeners.slice().forEach(listener => {
      try {
        listener();
      } catch (error) {
        // One listener's failure is that host's to report; the others still hear about it.
        console.error(error);
      }
    });
  }, 0);
};

export const subscribeCopilotAvailability = (listener: () => void): (() => void) => {
  listeners.push(listener);
  return () => {
    const index: number = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
};
